import { APP_BASE_HREF } from '@angular/common';
import { ChangeDetectorRef, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MatSnackBar, MatSnackBarConfig } from '@angular/material/snack-bar';
import { NavigationEnd, Router } from '@angular/router';
import { NotificationService } from '@planone/bau365/util-notification';
import { SnackBarComponent } from '@planone/shared/ui-elements';
import { WindowRef } from '@planone/shared/util-browser-api';
import { SnackBarType } from '@planone/shared/util-interfaces';
import { KeycloakEvent, KeycloakEventType, KeycloakService } from 'keycloak-angular';
import { filter, map, Observable, startWith, Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'bau365-shell-shell',
  templateUrl: './shell.component.html',
})
export class ShellComponent implements OnInit, OnDestroy {
  isLoggedIn = false;

  showFooter$: Observable<boolean>;

  private unsubscribe: Subject<void> = new Subject<void>();

  constructor(
    private snackBar: MatSnackBar,
    private keycloakService: KeycloakService,
    private notificationSerivce: NotificationService,
    private router: Router,
    private changeDetectorRef: ChangeDetectorRef,
    private windowRef: WindowRef,
    @Inject(APP_BASE_HREF) private baseHref: string
  ) {
    this.showFooter$ = this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      map((event) => (event as NavigationEnd).url),
      startWith(this.router.url),
      map((url) => {
        const fullUrl = new URL(url, 'https://test.local');
        return !fullUrl.pathname.endsWith('/hall-plan');
      })
    );
  }

  ngOnInit(): void {
    this.isLoggedIn = this.keycloakService.isLoggedIn();

    this.notificationSerivce.notificationContent$
      .pipe(
        takeUntil(this.unsubscribe),
        filter((notification) => !!notification.message)
      )
      .subscribe((notification) => {
        this.openSnackBar(notification.type, notification.message, notification.action, notification.closable);
      });

    this.keycloakService.keycloakEvents$.pipe(takeUntil(this.unsubscribe)).subscribe({
      next: (keycloakEvent: KeycloakEvent) => {
        // Ref: https://github.com/mauriciovigolo/keycloak-angular/issues/336
        if (
          KeycloakEventType.OnAuthRefreshError === keycloakEvent.type ||
          KeycloakEventType.OnAuthLogout === keycloakEvent.type
        ) {
          this.isLoggedIn = false;

          this.changeDetectorRef.detectChanges();

          this.router.navigateByUrl('/logout');
        }
      },
    });
  }

  ngOnDestroy() {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  logout() {
    this.keycloakService.getKeycloakInstance().logout({
      redirectUri: this.windowRef.nativeWindow.location.href.replace(this.router.url, '') + '/logout',
    });
  }

  openSnackBar(
    sbType: string,
    message: string,
    action: string | undefined = undefined,
    closable: boolean | undefined = false
  ) {
    let snackbarConfig: MatSnackBarConfig = {
      horizontalPosition: 'end',
      data: {
        type: sbType,
        message: message,
        action: action,
        closable: closable,
      },
    };
    switch (sbType) {
      case SnackBarType.success:
        snackbarConfig = { ...snackbarConfig, panelClass: 'snackbar--success', duration: 3000 };
        break;
      case SnackBarType.failure:
        snackbarConfig = { ...snackbarConfig, panelClass: 'snackbar--failure' };
        break;
      case SnackBarType.warning:
        snackbarConfig = { ...snackbarConfig, panelClass: 'snackbar--warning', duration: 3000 };
        break;
      case SnackBarType.neutral:
      default:
        snackbarConfig = { ...snackbarConfig, panelClass: 'snackbar--neutral', duration: 3000 };
        break;
    }

    this.snackBar.openFromComponent(SnackBarComponent, snackbarConfig);
  }
}
